import { canUploadAgain } from '../../../utils/submissionStatus';

const resolveStudentName = (student, fallbackId) =>
  student?.name ||
  student?.studentName ||
  student?.firstName ||
  (fallbackId != null ? `Student ${fallbackId}` : 'Student');

/** Flatten + dedupe students from useActiveClassData() class objects. */
export const flattenClassStudents = (classes = []) => {
  const seen = new Set();
  const students = [];

  (classes || []).forEach((clazz) => {
    (clazz?.students || []).forEach((student) => {
      const id = student?.id ?? student?.studentId;
      if (!id || seen.has(String(id))) return;
      seen.add(String(id));
      const name = resolveStudentName(student, id);
      students.push({
        id,
        name,
        studentName: student.studentName || student.name || student.firstName || name,
        classId: clazz.id,
      });
    });
  });

  return students.sort((a, b) => a.name.localeCompare(b.name));
};

export const normalizeStudentRecord = (student, classId) => {
  const id = student?.id ?? student?.studentId;
  if (!id) return null;
  const name = resolveStudentName(student, id);
  return {
    id,
    name,
    studentName: student.studentName || student.name || student.firstName || name,
    classId: student.classId || classId,
  };
};

/** Students assigned to a task's classes — same source as TaskCard upload menu. */
export const getStudentsForAssignment = (assignment, classes = []) => {
  const classIds = assignment?.classIds || [];
  if (!classIds.length) return [];

  const seen = new Set();
  const students = [];

  classIds.forEach((classId) => {
    const clazz = classes.find((item) => String(item?.id) === String(classId));
    (clazz?.students || []).forEach((student) => {
      const normalized = normalizeStudentRecord(student, classId);
      if (!normalized || seen.has(String(normalized.id))) return;
      seen.add(String(normalized.id));
      students.push(normalized);
    });
  });

  return students.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Whether a roster row is a handed-in response, by the same reading the server
 * uses for its roster and for the batch's one-response-per-student rule:
 * closing a task parks the real status in `statusBeforeClosed`, and a response
 * that a failed JeddAI attempt put back to DRAFT still carries its `submittedAt`.
 *
 * A bare DRAFT — the shell a teacher upload or the Organise step creates before
 * any work exists — is not a response, so the student stays on the list.
 */
export const hasHandedInResponse = (submission) => {
  const status = String(submission?.status || '').toUpperCase();
  const effective =
    status === 'CLOSED'
      ? String(submission?.statusBeforeClosed || '').toUpperCase() || 'CLOSED'
      : status;
  if (!effective || effective === 'DELETED') return false;
  return effective !== 'DRAFT' || Boolean(submission?.submittedAt);
};

const studentIdKey = (id) =>
  id === undefined || id === null || id === '' ? null : String(id);

/**
 * Students whose response a teacher reassigned and who are waiting to redo it,
 * by the server's reading (06 §5.8, `SubmissionsReadService.handIns`): the
 * student's only handed-in row on the task is RESUBMISSION_REQUESTED. A bulk
 * upload routes their pages into that row as its resubmission (v1
 * `POST /submissions/batch` and the v2 commit alike) rather than minting a
 * second response.
 *
 * Only where the server says this teacher may upload the row again
 * (`canUploadAgain`, the same rule the task card's Upload responses menu uses):
 * after routing, every answer save, question split and submit the wizard makes
 * goes through the server's upload-again guard, so a row it refuses (a student's
 * own online hand-in that was reassigned, a co-teacher without the claim, a
 * revision the student has started) would commit and then 403 on Review. Such a
 * student stays excluded as handed in.
 */
export const resubmissionStudentIds = (submissions = []) => {
  const handedInByStudent = new Map();
  (submissions || []).filter(hasHandedInResponse).forEach((sub) => {
    const id = studentIdKey(sub?.studentId);
    if (!id) return;
    handedInByStudent.set(id, [...(handedInByStudent.get(id) || []), sub]);
  });
  const ids = new Set();
  handedInByStudent.forEach((rows, id) => {
    if (rows.length === 1 && canUploadAgain(rows[0])) {
      ids.add(id);
    }
  });
  return ids;
};

/**
 * Drop students who already have a handed-in response (bulk upload is for Not
 * Submitted). See hasHandedInResponse for what counts. A reassigned student
 * awaiting their redo (resubmissionStudentIds) stays, marked
 * `resubmission: true`, so the picker can say "Resubmission requested".
 */
export const excludeStudentsWithSubmissions = (
  students = [],
  submissions = [],
) => {
  const resubmissions = resubmissionStudentIds(submissions);
  const occupied = new Set(
    (submissions || [])
      .filter(hasHandedInResponse)
      .map((sub) => studentIdKey(sub?.studentId))
      .filter((id) => id && id !== 'undefined' && id !== 'null' && !resubmissions.has(id)),
  );
  if (!occupied.size && !resubmissions.size) return students;
  return (students || [])
    .filter((student) => !occupied.has(String(student?.id ?? student?.studentId)))
    .map((student) =>
      resubmissions.has(String(student?.id ?? student?.studentId))
        ? { ...student, resubmission: true }
        : student,
    );
};

export const mergeStudentRecords = (preferred = [], fallback = []) => {
  const byId = new Map(
    fallback.map((student) => [String(student.id), student]),
  );

  return preferred.map((student) => {
    const base = byId.get(String(student.id));
    const name =
      student.name ||
      student.studentName ||
      base?.name ||
      base?.studentName ||
      'Student';
    return {
      ...base,
      ...student,
      id: student.id,
      name,
      studentName:
        student.studentName ||
        student.name ||
        base?.studentName ||
        base?.name ||
        name,
      classId: student.classId || base?.classId,
    };
  });
};
